/**
 * Chase camera for v1.1 (GDD §2; concept L1464–L1470): 34 m behind and 11.5 m above the jet along 70% of its
 * heading, damped per axis, looking 46 m ahead, rolling with 30% of the jet's bank, and widening the lens with speed.
 */
import { damp, lerp } from '../core/math.ts';
import type { FlightState } from '../sim/flight.ts';
import type { CameraRig, RigPose } from './rigs.ts';

const YAW_SHARE = 0.7;
const BACK = 34;
const UP = 11.5;
const AHEAD = 46;
const LOOK_UP = 1.5;
const ROLL_SHARE = 0.3;
const DAMP = [3.2, 2.5, 6] as const;

/** Lens widens with speed around the canyon's 54 m/s cruise (concept L1469). */
export const chaseFov = (speed: number): number => 58 + (speed - 54) * 0.6;

export interface ChaseRig extends CameraRig {
  /** Damped camera position at the current and previous step. */
  readonly pos: [number, number, number];
  readonly prev: [number, number, number];
}

export function createChaseRig(flight: FlightState): ChaseRig {
  // The concept starts the camera at (x, 30, 30) and lets it settle (concept L857).
  const pos: [number, number, number] = [flight.x, 30, 30];
  const prev: [number, number, number] = [flight.x, 30, 30];
  return {
    name: 'chase',
    pos,
    prev,
    step(f, dt) {
      prev[0] = pos[0];
      prev[1] = pos[1];
      prev[2] = pos[2];
      const a = f.yaw * YAW_SHARE;
      // fwd = (0, 0, −1) turned by a about +y: (−sin a, 0, −cos a); the goal is BACK metres behind along it.
      pos[0] = damp(pos[0], f.x + BACK * Math.sin(a), DAMP[0], dt);
      pos[1] = damp(pos[1], f.y + UP, DAMP[1], dt);
      pos[2] = damp(pos[2], f.z + BACK * Math.cos(a), DAMP[2], dt);
    },
    shiftZ(dz) {
      pos[2] += dz;
      prev[2] += dz;
    },
    pose(f, alpha, _clock, out: RigPose) {
      const a = f.yaw * YAW_SHARE;
      out.pos[0] = lerp(prev[0], pos[0], alpha);
      out.pos[1] = lerp(prev[1], pos[1], alpha);
      out.pos[2] = lerp(prev[2], pos[2], alpha);
      out.target[0] = f.x - AHEAD * Math.sin(a);
      out.target[1] = f.y + LOOK_UP;
      out.target[2] = f.z - AHEAD * Math.cos(a);
      out.roll = f.roll * ROLL_SHARE;
      out.fov = chaseFov(f.speed);
      out.sun[0] = f.x;
      out.sun[1] = f.z - 90;
      return out;
    },
  };
}
